import {
  IDetailedPromoIndicators,
  IPromoIndicators,
  IUnitMeasure,
  IUnitMeasureDetail,
} from 'core/model/interfaces/product.interface';
import { IDetailedPromotion, ITierPromotionDetail } from 'core/model/interfaces/promotion.interface';
import { getPresentationTextForDetail } from 'shared/utils/products.utils';
import { IDetailedPromotionDto, IProductDTO, IPromotionsDTO, IUnitMeasureDetailDTO } from './catalogs.dto';

export const getPromoIndicatorsForUnitMeasureDTO = (promotions: IPromotionsDTO): IPromoIndicators => {
  const {
    hasExclusive: isExclusive,
    hasSimpleBonus,
    hasTieredBonus,
    hasComboDiscount,
    hasComboBonus,
    hasSimpleDiscount,
    hasTieredDiscount,
    maxTieredDiscount,
  } = promotions;

  return {
    hasAnyDiscount: hasSimpleDiscount || hasTieredDiscount,
    hasAnyCombo: hasComboBonus || hasComboDiscount,
    hasAnyBonus: hasSimpleBonus || hasTieredBonus,
    maxDiscount: maxTieredDiscount || undefined,
    isExclusive,
    hasSimpleDiscount,
    hasTieredDiscount,
    hasSimpleBonus,
    hasTieredBonus,
    hasComboDiscount,
    hasComboBonus,
  };
};

export const getPromoIndicatorsForUnitMeasureDetailDTO = (
  promotions: IDetailedPromotionDto,
): IDetailedPromoIndicators => {
  const {
    hasExclusive: isExclusive,
    hasComboBonus,
    hasComboDiscount,
    hasSimpleBonus,
    hasSimpleDiscount,
    hasTieredBonus,
    hasTieredDiscount,
    maxTieredDiscount: maxDiscount,
  } = promotions;

  return {
    hasAnyDiscount: hasSimpleDiscount || hasTieredDiscount,
    hasAnyCombo: hasComboBonus || hasComboDiscount,
    hasAnyBonus: hasSimpleBonus || hasTieredBonus,
    maxDiscount: maxDiscount || undefined,
    isExclusive,
    hasSimpleBonus,
    hasTieredBonus,
    hasComboDiscount,
    hasComboBonus,
    hasSimpleDiscount,
    hasTieredDiscount,
  };
};

export const buildDetailedPromotions = (data: IDetailedPromotionDto): IDetailedPromotion => {
  const promotions: IDetailedPromotion = {
    simple: [],
    tiered: [],
    combo: [],
  };
  const { simple = [], tiered = [], combo = [] } = data;
  promotions.simple = simple.map(simplePromotion => ({
    tag: simplePromotion.type,
    id: simplePromotion.id,
  }));

  promotions.tiered = tiered.map(tieredPromotion => {
    const tiers: Array<ITierPromotionDetail> = [];

    // NOSONAR
    tieredPromotion.conditions.forEach(limit => {
      const { discount, min: lower, max: upper, bonus } = limit;

      tiers.push({
        maxQuantity: upper,
        minQuantity: lower,
        discount,
        bonus,
      });
    });

    return {
      id: tieredPromotion.id,
      limitType: tieredPromotion.limitType,
      tiers,
      tag: tieredPromotion.type,
    };
  });

  promotions.combo = combo.map(comboPromotion => ({
    tag: comboPromotion.type,
    description: comboPromotion.description,
    id: comboPromotion.id,
  }));
  return promotions;
};

export const getUnitMeasureDetailFromDTO = (
  unitMeasure: IUnitMeasureDetailDTO,
  qtyOfUnitMeasures: number,
): IUnitMeasureDetail => {
  const promoIndicators = getPromoIndicatorsForUnitMeasureDetailDTO(unitMeasure.promotions);

  return {
    stock: unitMeasure.stock,
    selected: unitMeasure.isBase,
    code: unitMeasure.id,
    default: unitMeasure.isBase,
    name: unitMeasure.name,
    pluralName: unitMeasure.pluralName,
    presentation: getPresentationTextForDetail(unitMeasure, qtyOfUnitMeasures),
    price: unitMeasure.price.amount,
    originalPrice: unitMeasure.price.original,
    promoIndicators,
    promotions: buildDetailedPromotions(unitMeasure.promotions),
  };
};

export const getUnitMeasureFromDTO = (product: IProductDTO): IUnitMeasure => {
  const promoIndicators = getPromoIndicatorsForUnitMeasureDTO(product.promotions);

  return {
    code: product.presentation.id,
    default: product.presentation.isBase,
    name: product.presentation.name,
    pluralName: product.presentation.pluralName,
    presentation: product.presentation.conversionFactor ?? '',
    price: product.price.amount,
    originalPrice: product.price.original,
    promoIndicators,
  };
};
